import { Navigate, Outlet, useLocation } from 'react-router';

import Sidebar from '@/components/app/Sidebar';
import { SidebarProvider, SidebarTrigger, useSidebar } from '@/components/ui/sidebar';

import { useAuth } from '@/hooks/useAuth';
import { Separator } from '@/components/ui/separator';
import AppBreadcrumb from '@/components/app/AppBreadcrumb';
import { Notification } from '@/components/app/Notification';

import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import SocketContextProvider from '@/contexts/SocketContext';
import { Toaster } from '@/components/ui/toaster';
import { UserMenu } from '@/components/app/UserMenu';
import { NextStepProvider, NextStepReact } from 'nextstepjs';
import { useReactRouterAdapter } from 'nextstepjs/adapters/react-router';
import { steps } from '@/walkthrough/november-25-update';

export default function AuthMiddleware() {
  const location = useLocation();
  const { isAuthenticated, loading } = useAuth();
  const isIndependentModuleRoute =
    location.pathname.startsWith('/energie') || location.pathname.startsWith('/frigos');

  if (!isAuthenticated && !loading) {
    return <Navigate state={{ from: location }} to="/auth/sign-in" />;
  }

  const content = (
    <>
      <Sidebar />
      <Layout>
        <header className="px-12 py-3 flex justify-between items-center">
          <div className="flex h-5 items-center gap-x-3 relative">
            <SidebarTrigger className="md:absolute md:-left-12" />
            <Separator orientation="vertical" />
            <AppBreadcrumb />
          </div>
          <div className="flex items-center gap-x-3">
            {!isIndependentModuleRoute && <Notification />}
            <UserMenu />
          </div>
        </header>
        <section className="border h-full">
          <NextStepProvider>
            <NextStepReact navigationAdapter={useReactRouterAdapter} steps={steps}>
              <Outlet />
            </NextStepReact>
          </NextStepProvider>
          <Toaster />
        </section>
      </Layout>
      <ReactQueryDevtools buttonPosition="bottom-left" />
    </>
  );

  return (
    <SidebarProvider>
      {isIndependentModuleRoute ? content : <SocketContextProvider>{content}</SocketContextProvider>}
    </SidebarProvider>
  );
}

const Layout = ({ children }: { children: React.ReactNode }) => {
  const { open } = useSidebar();
  const sidebarWidth = open ? '17rem' : '3rem';
  return <main className={`flex flex-col flex-1 md:max-w-[calc(100vw-${sidebarWidth})]`}>{children}</main>;
};
